import { CardList } from '@eventbrite/eds-card-list';
import { Vertical } from '@eventbrite/eds-layout';
import { gettext } from '@eventbrite/i18n';
import {
    ChoiceCard,
    ExpansionPanel,
    ExpansionPanelContent,
    ExpansionPanelTrigger,
    Typography,
} from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import TierPile from '../../components/reserved/TierPile';
import { TicketVariantForSelectionListProps } from '../../components/ticketsSelectionList/constants';
import { MultipleTicketVariantsCardProps } from '../../components/ticketsSelectionList/MultipleTicketVariantsCard';
import TicketVariantCard from '../../components/ticketsSelectionList/TicketVariantCard/TicketVariantCard';
import { SMALL_COLOR_CIRCLE_SIZE } from '../../components/tierColorCircle/constants';
import { TierColorCircle } from '../../components/tierColorCircle/TierColorCircle';
import { Ticket } from '../../models/tickets';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import defaultStyles from './ReservedTicketSelectionCard.module.scss';
import rebrandStyles from './ReservedTicketSelectionCard.rebranding.module.scss';
import { shouldShowPromoCodePills } from './utils/shouldShowPromoCodePills';
import { isTierVariantBestAvailable } from './utils/ticketUtils';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export interface ReservedTicketSelectionCardProps {
    onQuantityChange: (payload: {
        variantId?: string;
        id?: string;
        quantity: number;
    }) => void;
    ticket: Ticket &
        Pick<MultipleTicketVariantsCardProps, 'priceRange' | 'variants'>;
    isAppRebrandActive?: boolean;
}

export const ReservedTicketSelectionCard: FunctionComponent<
    ReservedTicketSelectionCardProps
> = ({ onQuantityChange, ticket, isAppRebrandActive = false }) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const [isExpanded, setIsExpanded] = useState(true);

    const _handleQuantityChange = (
        ticketId: string,
        variantId: string | null,
        quantity: number,
    ) => {
        const payload = {
            id: ticketId,
            quantity,
            ...(variantId ? { variantId } : {}),
        };

        onQuantityChange(payload);
    };

    const items = ticket.variants.map(
        (variant: TicketVariantForSelectionListProps, index) => {
            const { amountOff, discountType, onSaleStatus } = variant;
            const { showDiscountedPill, showUnlockedPill } =
                shouldShowPromoCodePills({
                    amountOff,
                    discountType,
                    onSaleStatus,
                });

            const onQuantityChange = (quantity: number) => {
                _handleQuantityChange(ticket.id, variant.id, quantity);
            };

            const { decoration, name } = isTierVariantBestAvailable(variant)
                ? {
                      decoration: (
                          <TierPile
                              variants={ticket.variants}
                              iconSize={SMALL_COLOR_CIRCLE_SIZE}
                          />
                      ),
                      name: gettext('Best Available'),
                  }
                : {
                      decoration: (
                          <div className={styles.circleWrapper}>
                              <TierColorCircle
                                  tierColor={variant.color ?? ''}
                                  colorCircleSize={SMALL_COLOR_CIRCLE_SIZE}
                              />
                          </div>
                      ),
                      name: variant.name,
                  };

            const isBestAvailableSelected = ticket.variants.some(
                (variant) =>
                    isTierVariantBestAvailable(variant) &&
                    variant.selectedQuantity > 0,
            );

            const isNotBestAvailableSelected = ticket.variants.some(
                (variant) =>
                    !isTierVariantBestAvailable(variant) &&
                    variant.selectedQuantity > 0,
            );

            const isDisabled =
                (isBestAvailableSelected &&
                    !isTierVariantBestAvailable(variant)) ||
                (isNotBestAvailableSelected &&
                    isTierVariantBestAvailable(variant));

            return {
                id: variant.id,
                content: (
                    <TicketVariantCard
                        category={variant.category}
                        cost={variant.cost}
                        currencyFormat={ticket.currencyFormat}
                        currencySymbol={variant.currencySymbol ?? ''}
                        description={variant.description}
                        donationAmount={variant.donationAmount}
                        feesAndTaxes={variant.feeAndTax ?? []}
                        formattedSalesEndDate={variant.salesEnd ?? ''}
                        formattedSalesStartDate={variant.salesStart ?? ''}
                        headerDecoration={decoration}
                        hideSaleDates={variant.hideSaleDates}
                        hideSelect={variant.hideSelect}
                        isAppRebrandActive={isAppRebrandActive}
                        isDisabled={isDisabled}
                        isLastItem={index === ticket.variants.length - 1}
                        includeFee={variant.includeFee}
                        includeTax={variant.includeTax}
                        isBOGOTicket={false}
                        isCurrencySuffix={false}
                        isDonation={false}
                        name={name}
                        onQuantityChange={onQuantityChange}
                        price={variant.displayPrice ?? null}
                        priceWithoutDiscounts={variant.primaryPrice ?? ''}
                        remainingQuantity={variant.quantityRemaining}
                        selectableQuantities={variant.ticketOptionsRange}
                        selectedQuantity={variant.selectedQuantity}
                        showDiscountedPill={showDiscountedPill}
                        showUnlockedPill={showUnlockedPill}
                        ticketId={variant.id}
                        ticketStatus={variant.statusKey}
                        urgencySignals={null}
                    />
                ),
            };
        },
    );

    const priceDisplay =
        ticket.variants.length === 1
            ? ticket.variants[0].displayPrice
            : ticket.priceRange;

    const headerClassNames = classNames('eds-l-pad-hor-4', styles.header, {
        ['eds-l-pad-bot-4']: !isExpanded,
    });

    return (
        <ChoiceCard
            className={classNames(styles.card, 'eds-l-mar-top-4')}
            data-testid="multiple-ticket-variants-card"
        >
            <ExpansionPanel
                className={styles.expansionPanel}
                defaultOpen
                onOpenChange={(open) => setIsExpanded(open)}
                variant="ghost"
            >
                <ExpansionPanelTrigger className={styles.expansionPanelTrigger}>
                    <Vertical className={headerClassNames}>
                        <Typography
                            variant="body-lg"
                            color="neutral-800"
                            className={styles.name}
                        >
                            {ticket.name}
                        </Typography>
                        <div className={classNames(styles.tierPile)}>
                            <TierPile
                                variants={ticket.variants}
                                iconSize={SMALL_COLOR_CIRCLE_SIZE}
                            />
                            <Typography
                                variant="body-md"
                                color="neutral-600"
                                className={styles.priceRange}
                            >
                                {priceDisplay}
                            </Typography>
                        </div>
                    </Vertical>
                </ExpansionPanelTrigger>
                <ExpansionPanelContent>
                    <CardList items={items} />
                </ExpansionPanelContent>
            </ExpansionPanel>
        </ChoiceCard>
    );
};
